import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logoutUser } from '../services/userService';
import NotificationBell from './NotificationBell';

const Navbar = ({ user, userData }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showUserTypeModal, setShowUserTypeModal] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logoutUser();
  };

  const handleUserTypeSelection = (userType) => {
    setShowUserTypeModal(false);
    navigate(`/register?type=${userType}`);
  };

  return (
    <>
      <nav className="bg-white/98 backdrop-blur-lg shadow-2xl border-b border-gradient-to-r from-blue-200/30 to-purple-200/30 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Unified Brand Logo */}
            <div className="flex items-center shrink-0">
              <Link to="/" className="flex items-center group shrink-0">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200/50 shadow-sm hover:shadow-md transition-all duration-300 shrink-0 whitespace-nowrap">
                  <img
                    src="/udaan_jobs.png"
                    alt="Udaan Jobs Logo"
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      // Fallback to gradient icon if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback Icon */}
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 flex items-center justify-center rounded-lg" style={{ display: 'none' }}>
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                  </div>
                  <h1 className="text-lg xl:text-xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent whitespace-nowrap shrink-0">
                    Udaan Jobs
                  </h1>
                </div>
              </Link>
            </div>

            {/* Enhanced Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3 lg:gap-6 shrink-0">
              {user ? (
                <>
                  <div className="flex items-center gap-3 lg:gap-5 shrink-0">
                    {/* Compact Horizontal Executive User Info */}
                    <div className="flex items-center gap-2.5 py-1.5 px-3 rounded-xl bg-gradient-to-r from-gray-50 to-blue-50/60 border border-gray-200/60 shadow-2xs shrink-0 whitespace-nowrap">
                      <div className="relative">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 rounded-lg flex items-center justify-center text-white font-extrabold text-xs shadow-xs shrink-0">
                          {userData?.firstName?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-800 tracking-tight">
                          Welcome, {userData?.firstName}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${userData?.userType === 'superadmin'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xs'
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-2xs'
                          }`}>
                          {userData?.userType === 'jobseeker' ? 'Job Seeker' : userData?.userType === 'superadmin' ? 'Super Admin' : userData?.userType === 'franchise' ? 'Franchise' : 'Company'}
                        </span>
                      </div>
                    </div>

                    {/* Enhanced Navigation Links */}
                    <div className="flex items-center gap-1.5 lg:gap-2 shrink-0">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2 text-gray-700 hover:text-blue-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-blue-200/50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0a2 2 0 012-2h6l2 2h6a2 2 0 012 2v1" />
                        </svg>
                        Dashboard
                      </Link>
                      {/* Show Companies only for superadmin and jobseeker with payment */}
                      {(userData?.userType === 'superadmin' || (userData?.userType === 'jobseeker' && userData?.paymentId)) && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/companies' : '/companies'}
                          className="flex items-center gap-2 text-gray-700 hover:text-purple-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-purple-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          Companies
                        </Link>
                      )}
                      {(userData?.userType === 'employer' || userData?.userType === 'superadmin') && (
                        <Link
                          to="/database"
                          className="flex items-center gap-2 text-gray-700 hover:text-indigo-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-indigo-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Database
                        </Link>
                      )}
                      {userData?.userType === 'franchise' && (
                        <Link
                          to="/franchise/applications"
                          className="flex items-center gap-2 text-gray-700 hover:text-indigo-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-indigo-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Applications
                        </Link>
                      )}
                      {userData?.userType !== 'agent' && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/packages' : '/packages'}
                          className="flex items-center gap-2 text-gray-700 hover:text-green-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-green-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          Packages
                        </Link>
                      )}
                      {userData?.userType !== 'agent' && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/marketing' : '/marketing'}
                          className="flex items-center gap-2 text-gray-700 hover:text-pink-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-pink-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                          </svg>
                          Marketing
                        </Link>
                      )}
                      {userData?.userType === 'employer' && (
                        <Link
                          to="/billing-history"
                          className="flex items-center gap-2 text-gray-700 hover:text-orange-600 px-4 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-orange-200/50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Billing
                        </Link>
                      )}

                      {/* Notification Bell */}
                      <NotificationBell user={user} />
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:from-red-600 hover:via-red-700 hover:to-red-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 border border-red-400/20"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-700 hover:text-blue-600 px-5 py-2.5 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-sm font-semibold transition-all duration-300 hover:shadow-md hover:scale-105 border border-transparent hover:border-blue-200/50"
                  >
                    Login
                  </Link>
                  <button
                    onClick={() => setShowUserTypeModal(true)}
                    className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 border border-blue-400/20"
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      Register
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Enhanced Mobile menu button */}
            <div className="md:hidden flex items-center gap-2">
              {user && <NotificationBell user={user} />}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-3 rounded-xl text-gray-600 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 hover:shadow-md hover:scale-105 border border-gray-200/50 hover:border-blue-200/50"
              >
                <svg className="h-6 w-6 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ transform: isMenuOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Enhanced Mobile menu */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-4 pt-4 pb-6 space-y-3 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 border-t border-gradient-to-r from-blue-200/50 to-purple-200/50 backdrop-blur-sm">
                {user ? (
                  <>
                    <div className="flex items-center gap-4 px-4 py-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200/50 shadow-sm">
                      <div className="relative">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          {userData?.firstName?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        </div>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-900">
                          {userData?.firstName} {userData?.lastName}
                        </span>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm w-fit ${userData?.userType === 'superadmin'
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                          : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                          }`}>
                          <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                          {userData?.userType === 'jobseeker' ? 'Job Seeker' : userData?.userType === 'superadmin' ? 'Super Admin' : userData?.userType === 'franchise' ? 'Franchise' : 'Company'}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-blue-200/50"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0a2 2 0 012-2h6l2 2h6a2 2 0 012 2v1" />
                          </svg>
                        </div>
                        Dashboard
                      </Link>
                      {/* Show Companies only for superadmin and jobseeker with payment */}
                      {(userData?.userType === 'superadmin' || (userData?.userType === 'jobseeker' && userData?.paymentId)) && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/companies' : '/companies'}
                          className="flex items-center gap-3 text-gray-700 hover:text-purple-600 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-purple-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          Companies
                        </Link>
                      )}
                      {(userData?.userType === 'employer' || userData?.userType === 'superadmin') && (
                        <Link
                          to="/database"
                          className="flex items-center gap-3 text-gray-700 hover:text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-indigo-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          Database
                        </Link>
                      )}
                      {userData?.userType === 'franchise' && (
                        <Link
                          to="/franchise/applications"
                          className="flex items-center gap-3 text-gray-700 hover:text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-indigo-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          Applications
                        </Link>
                      )}
                      {userData?.userType !== 'agent' && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/packages' : '/packages'}
                          className="flex items-center gap-3 text-gray-700 hover:text-green-600 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-green-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          </div>
                          Packages
                        </Link>
                      )}
                      {userData?.userType !== 'agent' && (
                        <Link
                          to={userData?.userType === 'superadmin' ? '/superadmin/marketing' : '/marketing'}
                          className="flex items-center gap-3 text-gray-700 hover:text-pink-600 hover:bg-gradient-to-r hover:from-pink-50 hover:to-rose-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-pink-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                          </div>
                          Marketing
                        </Link>
                      )}
                      {userData?.userType === 'employer' && (
                        <Link
                          to="/billing-history"
                          className="flex items-center gap-3 text-gray-700 hover:text-orange-600 hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-orange-200/50"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          Billing History
                        </Link>
                      )}

                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center gap-3 w-full text-left bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-3 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] mt-4"
                    >
                      <div className="w-8 h-8 bg-red-400/30 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="flex items-center gap-3 text-gray-700 hover:text-blue-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:shadow-md hover:scale-[1.02] border border-transparent hover:border-blue-200/50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </div>
                      Login
                    </Link>
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        setShowUserTypeModal(true);
                      }}
                      className="flex items-center gap-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white px-4 py-3 rounded-xl text-base font-semibold w-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                    >
                      <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      </div>
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* User Type Selection Modal - Outside nav for proper positioning */}
      {showUserTypeModal && (
        <>
          {/* Modal Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999]"
            onClick={() => setShowUserTypeModal(false)}
          ></div>

          {/* Modal Content */}
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none">
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300 scale-100 opacity-100 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 rounded-2xl overflow-hidden shadow-2xl mb-4 ring-2 ring-blue-200/50">
                  <img
                    src="/udaan_jobs.png"
                    alt="Udaan Jobs Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center" style={{ display: 'none' }}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Join Our Platform</h2>
                <p className="text-gray-600">Choose how you'd like to get started</p>
              </div>

              <div className="space-y-4">
                {/* Job Seeker Option */}
                <button
                  onClick={() => handleUserTypeSelection('jobseeker')}
                  className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-700">I'm looking for a job / मी नोकरी शोधत आहे</h3>
                      <p className="text-gray-600 text-sm">Find your dream job and advance your career / तुमची स्वप्नातील नोकरी शोधा आणि तुमच्या करिअरमध्ये प्रगती करा</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Employer Option */}
                <button
                  onClick={() => handleUserTypeSelection('employer')}
                  className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 group text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-700">I want to hire talent / मला उमेदवारांची भरती करायची आहे</h3>
                      <p className="text-gray-600 text-sm">Post jobs and find the perfect candidates / नोकऱ्या पोस्ट करा आणि योग्य उमेदवार शोधा</p>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Close Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowUserTypeModal(false)}
                  className="text-gray-500 hover:text-gray-700 font-medium transition-colors duration-200 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
